import { useUserStore } from '../stores/userStore'
import { useBookingStore } from '../stores/bookingStore'
import { useErrorStore } from '../stores/errorStore'
import { computed } from 'vue'
import { getToken } from '../services/tokenService'
import { getDefaultHeaders } from '../utils/languageUtils'

const BASE_URL = import.meta.env.VITE_API_BASE_URL

interface SeatProduct {
  passenger_id: string
  tariff_code: string
  type?: string
  cancelled?: boolean
  item_ref?: string
  product_code?: string
}

interface SubmitProductsSegment {
  origin: string
  origin_name: string
  destination: string
  destination_name: string
  start_validity_date: string
  start_validity_time: string
  direction: 'outbound' | 'inbound'
  service_name: string
  service_identifier: string
  items: Array<{
    product_code: string
    passenger_id: string
    tariff_code: string
  }>
}

interface BookingRequestBody {
  segments: SubmitProductsSegment[]
  passengers: Array<{
    id: string
    type: string
  }>
  currency: string
}

export const submitProducts = async (
  bookingNumber: string,
  segments: SubmitProductsSegment[]
): Promise<any> => {
  const errorStore = useErrorStore()
  const bookingStore = useBookingStore()
  
  if (!bookingNumber) {
    const error = 'Kunne ikke finne bestillingsnummer.'
    errorStore.setError(error)
    throw new Error(error)
  }

  // Ensure segments is an array and has valid products
  if (!Array.isArray(segments) || !segments.length || !segments[0].items?.length) {
    const error = 'Ingen produkter å sende inn.'
    errorStore.setError(error)
    throw new Error(error)
  }

  // Validate each segment has required fields
  for (const segment of segments) {
    if (!segment.items?.length || !segment.items[0].passenger_id || !segment.items[0].tariff_code) {
      const error = 'Ugyldig produkt data.'
      errorStore.setError(error)
      throw new Error(error)
    }
  }

  try {
    const token = await getToken(false, true)
    const headers = await getDefaultHeaders(token)
    const requestBody: BookingRequestBody = {
      segments,
      passengers: segments[0].items.map((item) => ({
        id: item.passenger_id,
        type: 'ADT'
      })),
      currency: 'NOK'
    }

    const response = await fetch(
      `${BASE_URL}/api/v2/booking/${bookingNumber}`,
      {
        method: 'PATCH',
        headers,
        body: JSON.stringify(requestBody),
      }
    )

    if (!response.ok) {
      const errorText = await response.text()
      console.error('[SeatReservation] Failed to submit products:', {
        status: response.status,
        statusText: response.statusText,
        errorText,
      })
      const error = 'Kunne ikke sende inn produkter.'
      errorStore.setError(error)
      throw new Error(error)
    }

    const data = await response.json()
    return data
  } catch (error) {
    console.error('[SeatReservation] Error in submitProducts:', error)
    throw error
  }
}

const nextRoute = computed(() => {
  const userStore = useUserStore()
  return userStore.isLoggedIn
    ? '/reiseplanlegger/betaling'
    : '/reiseplanlegger/personalia'
})

export { nextRoute }
